import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { WorkflowEventApiService } from '../services/workflow-event-api.service';
import { WorkflowExecutionApiService } from '../services/workflow-execution-api.service';
import { WorkflowApiService } from '../services/workflow-api.service';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { WorkflowEventRequest, WorkflowEventResponse, WorkflowEnrollmentResponse } from '../models/workflow-runtime.model';
import { WorkflowExecutionContinueResponse } from '../models/workflow-execution.model';
import { WorkflowResponse } from '../models/workflow.model';
import { EntitySelectComponent } from '../../../../shared/components/entity-select/entity-select.component';
import { WorkflowRecordSelect } from '../shared/workflow-record-select.helper';
import { ContactoApiService } from '../../../contactos/services/contacto-api.service';
import { EmpresaApiService } from '../../../empresas/services/empresa-api.service';
import { DealApiService } from '../../../deals/services/deal-api.service';
import { QuoteApiService } from '../../../quotes/services/quote-api.service';
import { TicketsApiService } from '../../../tickets/services/tickets-api.service';
import {
  workflowNodeSubtypeLabel,
  workflowNodeTypeLabel,
  workflowRecordLabel,
  workflowOperatorLabel,
  workflowStatusLabel
} from '../models/workflow-labels';
import { branchKeyLabel } from '../workflow-builder/models/node-catalog';

interface EventTemplate {
  label: string;
  eventType: string;
  objectType: string;
  sourceModule: string;
  payload: Record<string, any>;
}

@Component({
  selector: 'app-workflow-event-tester',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, FormsModule, EntitySelectComponent],
  templateUrl: './workflow-event-tester.component.html'
})
export class WorkflowEventTesterComponent implements OnInit, OnDestroy {
  /**
   * Etiquetas legibles: diccionario único del módulo (`models/workflow-labels.ts`). El resultado
   * del evento devuelve `objectType`, `status`, `nextNodeType` y `actionType` en crudo.
   */
  readonly recordText = workflowRecordLabel;
  readonly statusText = workflowStatusLabel;
  readonly nodeTypeText = workflowNodeTypeLabel;
  readonly nodeSubtypeText = workflowNodeSubtypeLabel;
  readonly operatorText = workflowOperatorLabel;

  branchLabel(key: string | null | undefined): string {
    return key ? branchKeyLabel(key) : '—';
  }

  workflowId: number | null = null;
  workflow: WorkflowResponse | null = null;

  form!: FormGroup;
  loading = false;
  error = '';
  result: WorkflowEventResponse | null = null;

  // Payload construido internamente desde la plantilla (el usuario ya no ve JSON).
  payload: Record<string, any> = {};

  // Selector de registro por nombre
  recordSelect!: WorkflowRecordSelect;
  recordDisplayText = '';

  // Continuar enrollment desde result
  continuingEnrollmentId: number | null = null;
  continueResults: Map<number, WorkflowExecutionContinueResponse> = new Map();
  continueErrors: Map<number, string> = new Map();

  // INC_CRM_020 (8) inc26-46 — `value` = clave exacta del backend, `label` = único texto traducible.
  readonly objectTypeOptions: { value: string; label: string }[] = [
    { value: 'CONTACT', label: 'Contacto' },
    { value: 'COMPANY', label: 'Empresa' },
    { value: 'DEAL', label: 'Negocio' },
    { value: 'QUOTE', label: 'Cotización' },
    { value: 'TICKET', label: 'Ticket' }
  ];

  readonly eventTemplates: EventTemplate[] = [
    {
      label: 'Contacto creado',
      eventType: 'CONTACT_CREATED',
      objectType: 'CONTACT',
      sourceModule: 'CONTACT',
      payload: { fullname: 'Juan Pérez', country: 'Peru' }
    },
    {
      label: 'Propiedad cambiada',
      eventType: 'CONTACT_PROPERTY_CHANGED',
      objectType: 'CONTACT',
      sourceModule: 'CONTACT',
      payload: { property: 'lifecycleStage', oldValue: 'LEAD', newValue: 'CUSTOMER' }
    },
    {
      label: 'Segmento ingresado',
      eventType: 'SEGMENT_ENTERED',
      objectType: 'CONTACT',
      sourceModule: 'SEGMENTATION',
      payload: { segmentId: 1 }
    },
    {
      label: 'Empresa creada',
      eventType: 'COMPANY_CREATED',
      objectType: 'COMPANY',
      sourceModule: 'COMPANY',
      payload: { name: 'Acme Corp' }
    },
    {
      label: 'Deal creado',
      eventType: 'DEAL_CREATED',
      objectType: 'DEAL',
      sourceModule: 'DEAL',
      payload: { amount: 5000, currency: 'USD' }
    },
    {
      label: 'Deal cambiado de etapa',
      eventType: 'DEAL_STAGE_CHANGED',
      objectType: 'DEAL',
      sourceModule: 'DEAL',
      payload: { fromStage: 'PROSPECTING', toStage: 'QUALIFIED' }
    },
    {
      label: 'Cotización firmada',
      eventType: 'QUOTE_SIGNED',
      objectType: 'QUOTE',
      sourceModule: 'QUOTE',
      payload: { total: 3500 }
    },
    {
      label: 'Reunión completada',
      eventType: 'MEETING_COMPLETED',
      objectType: 'CONTACT',
      sourceModule: 'MEETING',
      payload: { meetingType: 'DEMO' }
    }
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private eventApi: WorkflowEventApiService,
    private executionApi: WorkflowExecutionApiService,
    private workflowApi: WorkflowApiService,
    private authService: AuthService,
    private contactoApi: ContactoApiService,
    private empresaApi: EmpresaApiService,
    private dealApi: DealApiService,
    private quoteApi: QuoteApiService,
    private ticketsApi: TicketsApiService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.recordSelect = new WorkflowRecordSelect(
      this.contactoApi, this.empresaApi, this.dealApi, this.quoteApi, this.ticketsApi,
      () => this.form?.get('objectType')?.value,
      () => this.authService.getCurrentUser()?.businessId || this.businessId || 1
    );
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.workflowId = Number(id);
      this.loadWorkflow();
    }
    this.initForm();
    // Al cambiar el tipo de objeto, limpiar el registro seleccionado.
    this.form.get('objectType')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.form.patchValue({ objectId: null }, { emitEvent: false });
      this.recordDisplayText = '';
    });
  }

  onRecordSelected(item: any | null): void {
    this.recordDisplayText = item ? this.recordSelect.label(item) : '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get businessId(): number {
    return this.authService.getBusinessId() ?? 0;
  }

  private initForm(): void {
    this.form = this.fb.group({
      eventType:    ['CONTACT_CREATED', Validators.required],
      objectType:   ['CONTACT', Validators.required],
      objectId:     [null, [Validators.required, Validators.min(1)]],
      sourceModule: ['CONTACT'],
      sourceKey:    ['']
    });
  }

  private loadWorkflow(): void {
    if (!this.workflowId) return;
    this.workflowApi.getById(this.workflowId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (wf) => {
        this.workflow = wf;
        if (wf.objectType) this.form?.patchValue({ objectType: wf.objectType }, { emitEvent: false });
      },
      error: () => {}
    });
  }

  applyTemplate(tpl: EventTemplate): void {
    this.form.patchValue({
      eventType:    tpl.eventType,
      objectType:   tpl.objectType,
      sourceModule: tpl.sourceModule
    });
    // El payload de la plantilla se usa internamente; el usuario no ve JSON.
    this.payload = { ...tpl.payload };
    this.result = null;
    this.error = '';
  }

  sendEvent(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }

    this.loading = true;
    this.error = '';
    this.result = null;

    const v = this.form.value;
    const request: WorkflowEventRequest = {
      intIdBussines: this.businessId,
      eventType:    v.eventType.trim(),
      objectType:   v.objectType,
      objectId:     v.objectId,
      sourceModule: v.sourceModule || undefined,
      sourceKey:    v.sourceKey || undefined,
      payload:      this.payload ?? {}
    };

    this.eventApi.sendEvent(request).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.result = res;
        this.loading = false;
      },
      error: (err) => {
        const msg = err.error?.message ?? '';
        if (err.status === 403) {
          this.error = 'No tienes permisos para realizar esta acción.';
        } else if (msg.toLowerCase().includes('ticket')) {
          this.error = 'No se pudo procesar la solicitud sobre el ticket.';
        } else if (msg.toLowerCase().includes('objeto') || msg.toLowerCase().includes('object')) {
          this.error = 'El tipo de objeto del evento no coincide con el tipo base del workflow.';
        } else {
          this.error = msg || 'Error al enviar el evento.';
        }
        this.loading = false;
      }
    });
  }

  get resultEnrollmentCount(): number {
    return this.result?.createdEnrollments?.length ?? 0;
  }

  continueEnrollment(enrollment: WorkflowEnrollmentResponse): void {
    this.continuingEnrollmentId = enrollment.id;
    this.continueErrors.delete(enrollment.id);
    this.executionApi.continueByEnrollment(enrollment.id, {})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.continueResults.set(enrollment.id, res);
          this.continuingEnrollmentId = null;
        },
        error: (err) => {
          this.continueErrors.set(enrollment.id, err.error?.message || 'Error al continuar.');
          this.continuingEnrollmentId = null;
        }
      });
  }

  getContinueResult(enrollmentId: number): WorkflowExecutionContinueResponse | undefined {
    return this.continueResults.get(enrollmentId);
  }

  getContinueError(enrollmentId: number): string | undefined {
    return this.continueErrors.get(enrollmentId);
  }

  getContinueMessage(res: WorkflowExecutionContinueResponse): string {
    if (res.status === 'COMPLETED') return 'Workflow completado.';
    if (res.status === 'WAITING' || res.nextNodeType === 'WAIT') return 'La inscripción quedó en espera.';
    if (res.nextNodeType === 'END') return 'Llegó al nodo final.';
    return res.message ?? 'Ejecución avanzada.';
  }

  isWaitingResult(res: WorkflowExecutionContinueResponse): boolean {
    return res.status === 'WAITING' || res.nextNodeType === 'WAIT';
  }

  isActionResult(res: WorkflowExecutionContinueResponse): boolean {
    return !!(res.details?.['actionType'] || res.details?.['actionStatus']);
  }

  getActionStatusClass(status: string | undefined): string {
    switch (status?.toUpperCase()) {
      case 'SUCCESS': return 'bg-green-100 text-green-700';
      case 'FAILED':  return 'bg-red-100 text-red-700';
      case 'SKIPPED': return 'bg-gray-100 text-gray-500';
      default:        return 'bg-blue-100 text-blue-700';
    }
  }

  isFieldInvalid(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl?.invalid && ctrl?.touched);
  }
}
